import React from "react";
import Select, { OnChangeValue } from "react-select";
import { useField, useFormikContext } from "formik";
import { MultiSelectFieldConfig, FormFieldOption } from "../types";
import ErrorField from "../errorField";
import FormLabel from "../formLabel";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import { DEFAULT_INITIAL_VALUE_ARRAY } from "shared/utils/constants";
import sortBy from "lodash/sortBy";
import FormGroup from "../formGroup";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { ValidateMultiSelectedFormOption } from "../utils/resetOptionsForInvalidSelection";

function FormMultiSelectField({
  menuPortalTarget = document.body,
  isClearable = true,
  onChange,
  ...props
}: MultiSelectFieldConfig) {
  // todo:rr autoselectsingleoption is not working
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange, submitCount } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  React.useEffect(() => {
    if (props.markTouchedOnSubmit && !meta.touched && submitCount > 0) {
      setFieldTouched(props.name, true, false);
    }
  }, [meta.touched, props.markTouchedOnSubmit, props.name, setFieldTouched, submitCount]);

  const onChangeHandler = React.useCallback(
    async (options: OnChangeValue<FormFieldOption, true>) => {
      const formOptions = options as Array<FormFieldOption>;
      await setFieldValue(
        props.name,
        formOptions.map(o => o.value),
        false
      );
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
      onChange?.(formOptions);
    },
    [setFieldValue, props.name, meta.touched, validateOnChange, validateForm, onChange, setFieldTouched]
  );

  const getValue = React.useCallback((): FormFieldOption[] => {
    if (props.options) {
      const filteredOptions = props.options.filter(
        (option: FormFieldOption) => field.value?.indexOf(option.value) >= 0
      );

      const originalOrder = field.value?.map
        ? field.value
            ?.map((value: string) => {
              return filteredOptions.find(item => item.value === value);
            })
            .filter(Boolean)
        : filteredOptions;

      return props.sortAlphabetically ? sortBy(originalOrder, ({ label }) => label.toLowerCase()) : originalOrder;
    }
    return [];
  }, [field.value, props.options, props.sortAlphabetically]);

  React.useEffect(() => {
    if (props.autoSelectSingleOption && props.required && props.options?.length === 1) {
      setFieldValue(props.name, [props.options[0]?.value]);
    }
  }, [props.autoSelectSingleOption, props.name, props.options, props.required, setFieldValue]);

  return (
    <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel
        htmlFor={field.name}
        label={props.label}
        required={props.required}
        infoLabel={props.infoLabel}
        additionalLabel={props.additionalLabel}
      />
      {formContextData.isReadOnly ? (
        getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.TAGS, value: getValue().map(o => o.label) })
      ) : (
        <Select
          isClearable={isClearable}
          isMulti={true}
          name={field.name}
          value={getValue()}
          onChange={onChangeHandler}
          inputId={field.name}
          options={props.options}
          isDisabled={props.disabled}
          classNamePrefix="multiSelect"
          menuPlacement={props.menuPlacement}
          menuPortalTarget={menuPortalTarget}
          menuPosition={"fixed"}
          className={`multiSelect ${props.className}`}
        />
      )}
      <ErrorField meta={meta} />
      <ValidateMultiSelectedFormOption options={props.options} values={field.value} name={props.name} />
      {props.enableFieldReset && (
        <FieldResetWhenRemoved name={props.name} defaultInitialValue={DEFAULT_INITIAL_VALUE_ARRAY} />
      )}
    </FormGroup>
  );
}

export default FormMultiSelectField;
